import { useCallback, useEffect } from 'react';

import { create } from 'zustand';

import { Platform } from '../constants';
import { useGlobalContext } from '../contexts/global';
import { useWebView } from './webview';

export const enum AdType {
  interstitial = 'interstitial',
  reward = 'reward',
}

export const enum AdStatus {
  initial,
  loading,
  success,
  failed,
}

type AdStoreState = {
  status: AdStatus;
  setStatus: (status: AdStatus) => void;
};

export const useAdStore = create<AdStoreState>((set) => ({
  status: AdStatus.initial,
  setStatus: (status) => set({ status }),
}));

export const UNIT_IDS = {
  [Platform.android]: {
    reward: process.env.NEXT_PUBLIC_ADX_AOS_REWARD_UNIT_ID,
    interstitial: process.env.NEXT_PUBLIC_ADX_AOS_INTERSTITIAL_UNIT_ID,
  },
  [Platform.ios]: {
    reward: process.env.NEXT_PUBLIC_ADX_IOS_REWARD_UNIT_ID,
    interstitial: process.env.NEXT_PUBLIC_ADX_IOS_INTERSTITIAL_UNIT_ID,
  },
};

export const useAd = (adType: AdType) => {
  const { platform } = useGlobalContext();

  const { status, setStatus } = useAdStore();
  const { web, listen } = useWebView();

  const unitId =
    platform !== Platform.web ? UNIT_IDS[platform][adType] : undefined;

  const load = useCallback(() => {
    if (unitId) {
      web.loadAd(adType, unitId);
      setStatus(AdStatus.loading);
    }
  }, [adType, unitId]);

  const present = useCallback(
    (cb?: () => void) => {
      if (status !== AdStatus.success || !unitId) {
        return;
      }
      web.viewAd(adType, unitId);
      setStatus(AdStatus.initial);
      cb?.();
    },
    [status, adType, unitId]
  );

  useEffect(() => {
    const unlistenLoadedAd = listen('loadedAd', () => {
      setStatus(AdStatus.success);
    });

    const unlistenFailedToLoadAd = listen('failedToLoadAd', () => {
      setStatus(AdStatus.failed);
    });

    const unlistenClosedAd = listen('closedAd', () => {
      setStatus(AdStatus.initial);
    });

    const unlistenRewardedAd = listen('rewardedAd', () => {
      setStatus(AdStatus.initial);
    });

    return () => {
      unlistenLoadedAd();
      unlistenFailedToLoadAd();
      unlistenClosedAd();
      unlistenRewardedAd();
    };
  }, [unitId]);

  return { isReady: status === AdStatus.success, load, present };
};
